<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import type { ChatContext } from '@klingerator/shared';
import { useUiStore } from '@/stores/ui';
import { useChatStore } from '@/stores/chat';
import { useProductEditorStore } from '@/stores/productEditor';
import { formatDateTime } from '@/utils/format';
import { CHAT_ASK } from '@/utils/chatTools';
import ChatMessageItem from './ChatMessageItem.vue';

const ui = useUiStore();
const chat = useChatStore();
const editor = useProductEditorStore();
const route = useRoute();

const bodyEl = ref<HTMLElement | null>(null);
const inputEl = ref<HTMLTextAreaElement | null>(null);
const resizing = ref(false);

/** Wat de gebruiker open heeft — gaat mee als "huidige context" naar de assistent. */
const context = computed<ChatContext>(() => {
  const name = typeof route.name === 'string' ? route.name : null;
  const idParam = Number(route.params.id);
  const productRoutes = ['product-detail', 'line-detail', 'product-import'];
  const productId =
    name && productRoutes.includes(name) && Number.isInteger(idParam) && idParam > 0 ? idParam : null;
  const product = productId && editor.product?.id === productId ? editor.product : null;
  return {
    route: name,
    product_id: productId,
    product_name: product?.name ?? null,
    tab: name === 'product-detail' ? (typeof route.query.tab === 'string' ? route.query.tab : 'lines') : null,
    // Geladen in de editor → wijzigingen van de assistent komen als concept in het formulier;
    // dirty → server-acties op dit product worden geweigerd tot er is opgeslagen.
    editor_open: !!product,
    editor_dirty: !!product && editor.dirty,
  };
});

const suggestions = computed(() => {
  const base = context.value.product_id
    ? [
        'Vat dit product samen en zeg wat er nog ontbreekt.',
        'Vul de ontbrekende secties van de verkoopomschrijving in.',
        'Waarom kan ik dit product niet naar Unit4 opslaan?',
        'Welke aanpassingen moet ik doen zodat dit product valideert?',
      ]
    : [
        'Welke producten van mij staan op Gereed?',
        'Hoe werkt een samenstelling met sub-producten?',
        'Zoek het Unit4-artikel voor een Herion magneetventiel 24VDC.',
      ];
  return base;
});

// Knop "Uitvoeren" bij een aanbeveling: verstuurt de instructie met de context van nu
provide(CHAT_ASK, (text: string) => chat.send(text, context.value));
/**
 * Bericht met de meest recente aanbevelingen: die blijven bruikbaar, ook nadat je er één hebt
 * uitgevoerd (dan volgen er nieuwe berichten). Oudere aanbevelingen niet meer.
 */
const actionableIndex = computed(() => {
  for (let i = chat.messages.length - 1; i >= 0; i--)
    if (
      chat.messages[i].blocks.some(
        (b) => b.type === 'tool_call' && b.name === 'suggest_actions' && b.status !== 'error',
      )
    )
      return i;
  return -1;
});

// ---------- Versturen ----------
async function submit() {
  const text = chat.draft;
  if (!text.trim() || chat.streaming) return;
  await chat.send(text, context.value);
  await nextTick();
  inputEl.value?.focus();
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault();
    void submit();
  }
}
function autoGrow() {
  const el = inputEl.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
}
watch(
  () => chat.draft,
  () => nextTick(autoGrow),
);

// ---------- Meescrollen (alleen als de gebruiker onderaan zit) ----------
const stick = ref(true);
function onScroll() {
  const el = bodyEl.value;
  if (!el) return;
  stick.value = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
}
function scrollToBottom(force = false) {
  const el = bodyEl.value;
  if (!el || (!stick.value && !force)) return;
  el.scrollTop = el.scrollHeight;
}
watch(
  () => [chat.messages.length, chat.messages.at(-1)?.content.length, chat.messages.at(-1)?.blocks.length],
  () => nextTick(() => scrollToBottom()),
);
watch(
  () => chat.currentId,
  () => nextTick(() => scrollToBottom(true)),
);

// ---------- Breedte slepen (rand links) ----------
let dragWidth = 0;
function onResizeStart(e: PointerEvent) {
  if (e.button !== 0) return;
  e.preventDefault();
  resizing.value = true;
  dragWidth = ui.chatWidth;
  document.body.classList.add('kl-chat-resizing');
  window.addEventListener('pointermove', onResizeMove);
  window.addEventListener('pointerup', onResizeEnd, { once: true });
  window.addEventListener('pointercancel', onResizeEnd, { once: true });
}
function onResizeMove(e: PointerEvent) {
  if (!resizing.value) return;
  dragWidth = window.innerWidth - e.clientX;
  ui.setChatWidth(dragWidth, false);
}
function onResizeEnd() {
  if (!resizing.value) return;
  resizing.value = false;
  document.body.classList.remove('kl-chat-resizing');
  window.removeEventListener('pointermove', onResizeMove);
  ui.setChatWidth(dragWidth, true);
}
function onResizeKey(e: KeyboardEvent) {
  const step = e.shiftKey ? 80 : 20;
  if (e.key === 'ArrowLeft') ui.setChatWidth(ui.chatWidth + step);
  else if (e.key === 'ArrowRight') ui.setChatWidth(ui.chatWidth - step);
  else return;
  e.preventDefault();
}

async function removeConversation(id: number, title: string | null) {
  const ok = await ui.confirm({
    title: 'Gesprek verwijderen',
    message: `Wil je het gesprek "${title || 'Nieuw gesprek'}" verwijderen? Dit kan niet ongedaan worden gemaakt.`,
    confirmLabel: 'Verwijderen',
    variant: 'danger',
  });
  if (ok) await chat.remove(id);
}

onMounted(async () => {
  await chat.init();
  await nextTick();
  scrollToBottom(true);
  autoGrow();
  inputEl.value?.focus();
});
onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onResizeMove);
  document.body.classList.remove('kl-chat-resizing');
});
</script>

<template>
  <aside class="kl-chat" aria-label="Assistent" data-testid="chat-panel">
    <div
      class="kl-chat-resizer"
      :class="{ active: resizing }"
      role="separator"
      aria-orientation="vertical"
      aria-label="Breedte van de assistent aanpassen (pijltjestoetsen)"
      tabindex="0"
      title="Sleep om de breedte aan te passen"
      data-testid="chat-resizer"
      @pointerdown="onResizeStart"
      @keydown="onResizeKey"
    ></div>

    <header class="kl-chat-header">
      <i class="bi bi-robot text-primary fs-5" aria-hidden="true"></i>
      <div class="dropdown min-w-0 flex-grow-1">
        <button
          class="btn btn-link text-start text-decoration-none p-0 w-100 kl-chat-title dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          title="Gesprek kiezen"
        >
          <span class="fw-semibold text-primary d-block text-truncate">{{
            chat.current?.title || 'Assistent'
          }}</span>
        </button>
        <ul class="dropdown-menu shadow kl-chat-conversations">
          <li>
            <button class="dropdown-item" type="button" @click="chat.startNew()">
              <i class="bi bi-plus-lg me-2" aria-hidden="true"></i>Nieuw gesprek
            </button>
          </li>
          <li v-if="chat.conversations.length"><hr class="dropdown-divider" /></li>
          <li v-for="c in chat.conversations" :key="c.id" class="d-flex align-items-center">
            <button
              class="dropdown-item flex-grow-1 text-truncate"
              :class="{ active: c.id === chat.currentId }"
              type="button"
              :title="c.title || 'Nieuw gesprek'"
              @click="chat.select(c.id)"
            >
              <span class="d-block text-truncate">{{ c.title || 'Nieuw gesprek' }}</span>
              <small class="d-block opacity-75">{{ formatDateTime(c.updated_at) }}</small>
            </button>
            <button
              class="btn btn-link text-danger btn-sm me-1"
              type="button"
              :aria-label="`Gesprek '${c.title || 'Nieuw gesprek'}' verwijderen`"
              @click.stop="removeConversation(c.id, c.title)"
            >
              <i class="bi bi-trash" aria-hidden="true"></i>
            </button>
          </li>
        </ul>
      </div>
      <button
        class="btn btn-link text-primary p-1"
        type="button"
        title="Nieuw gesprek"
        aria-label="Nieuw gesprek"
        @click="chat.startNew()"
      >
        <i class="bi bi-plus-square" aria-hidden="true"></i>
      </button>
      <button
        class="btn btn-link text-primary p-1"
        type="button"
        title="Assistent verbergen"
        aria-label="Assistent verbergen"
        data-testid="chat-close"
        @click="ui.toggleChat(false)"
      >
        <i class="bi bi-x-lg" aria-hidden="true"></i>
      </button>
    </header>

    <div ref="bodyEl" class="kl-chat-body" @scroll="onScroll">
      <div v-if="chat.loading" class="text-center text-muted py-4">
        <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Gesprek laden…
      </div>
      <template v-else-if="!chat.messages.length">
        <div class="kl-chat-empty">
          <i class="bi bi-robot fs-1 text-primary" aria-hidden="true"></i>
          <p class="mb-2">
            Ik ken de Klingerator, de productentabel en de Unit4-configuratiedata. Vraag me iets over een
            product, een samenstelling, een sjabloon, een Unit4-artikel of een foutmelding.
          </p>
          <p class="small text-muted mb-2">
            Ik kan ook wijzigingen voorstellen: bij een geopend product komen die als concept in het formulier
            (jij slaat op); andere acties voer ik pas uit na jouw bevestiging.
          </p>
          <div class="d-grid gap-1">
            <button
              v-for="s in suggestions"
              :key="s"
              type="button"
              class="btn btn-outline-primary btn-sm text-start"
              @click="
                chat.draft = s;
                submit();
              "
            >
              {{ s }}
            </button>
          </div>
        </div>
      </template>
      <template v-else>
        <ChatMessageItem
          v-for="(m, i) in chat.messages"
          :key="m.id ?? `tmp-${i}`"
          :message="m"
          :actionable="i === actionableIndex"
        />
      </template>
      <div
        v-if="chat.error && !chat.messages.at(-1)?.error"
        class="alert alert-danger py-2 small"
        role="alert"
      >
        {{ chat.error }}
      </div>
    </div>

    <footer class="kl-chat-input">
      <form class="d-flex align-items-end gap-2" @submit.prevent="submit">
        <label for="kl-chat-text" class="visually-hidden">Bericht aan de assistent</label>
        <textarea
          id="kl-chat-text"
          ref="inputEl"
          v-model="chat.draft"
          class="form-control form-control-sm"
          rows="1"
          :placeholder="
            context.product_id
              ? 'Vraag iets over dit product… (Enter = versturen)'
              : 'Stel een vraag… (Enter = versturen)'
          "
          :disabled="chat.streaming"
          @keydown="onKeydown"
          @input="autoGrow"
        ></textarea>
        <button
          v-if="chat.streaming"
          type="button"
          class="btn btn-outline-danger btn-sm"
          title="Stoppen"
          aria-label="Antwoord stoppen"
          data-testid="chat-stop"
          @click="chat.stop()"
        >
          <i class="bi bi-stop-fill" aria-hidden="true"></i>
        </button>
        <button
          v-else
          type="submit"
          class="btn btn-primary btn-sm"
          :disabled="!chat.draft.trim()"
          title="Versturen"
          aria-label="Versturen"
          data-testid="chat-send"
        >
          <i class="bi bi-send-fill" aria-hidden="true"></i>
        </button>
      </form>
      <div v-if="context.product_id" class="small text-muted mt-1 text-truncate">
        <i class="bi bi-box-seam me-1" aria-hidden="true"></i>Context: product #{{ context.product_id
        }}<template v-if="context.product_name"> · {{ context.product_name }}</template
        ><template v-if="context.editor_dirty"> · <span class="text-warning">niet opgeslagen</span></template>
      </div>
    </footer>
  </aside>
</template>
